<template>
  <div class="product-mapping-tab">
    <div class="product-mapping-tab__section">
      <div class="flex flex-col gap-16">
        <div>
          <h3 class="section-title">Choose your Salesforce object</h3>
          <p class="section-description">
            Choose the object that holds your account product data
          </p>
        </div>
        <BittsCallout
          v-if="feedBlockReason === 'disconnected'"
          size="x-small"
          type="danger"
          data-testid="product-mapping-feed-disconnected"
          title="We're having trouble with your Salesforce account."
        >
          <template #subtitle>
            <p class="product-mapping-tab__callout-subtitle">
              Something is wrong with this connection. See more in the
              <BittsLink
                url="#"
                text="General tab"
                data-testid="product-mapping-feed-disconnected-link"
                @click="goToGeneralTab"
              />.
            </p>
          </template>
        </BittsCallout>
        <BittsCallout
          v-else-if="feedBlockReason === 'paused'"
          size="x-small"
          type="warning"
          data-testid="product-mapping-feed-paused"
          title="Syncing is paused for this data source"
          subtitle="Resume syncing, then come back to map your account products."
        />
        <template v-else>
          <BittsCallout
            v-if="isEditable"
            size="x-small"
            type="warning"
            title="This object cannot be changed after saving"
            subtitle="Pick the Salesforce object that holds your account-level product data. Once you click Save, this object can't be edited."
          />
          <BittsCallout
            v-if="catalogStatus === 'failed'"
            size="x-small"
            type="danger"
            data-testid="product-mapping-catalog-error"
            title="We couldn't load your Salesforce objects"
            subtitle="Please try again in a few moments."
            action-text="Retry"
            @bitts-callout-action="loadCatalog"
          />
          <p
            v-else-if="catalogStatus === 'empty'"
            data-testid="product-mapping-catalog-empty"
            class="section-description"
          >
            We couldn't find any Salesforce objects to map. Check that your
            Salesforce user can see the object that holds your account product
            data.
          </p>
          <BittsSelect
            v-else
            class="product-mapping-tab__object-select"
            data-testid="product-mapping-object-select"
            placeholder="Select a Salesforce object"
            :searchable="true"
            :filter-function="filterObjectOptions"
            :loading="catalogStatus === 'loading'"
            :none-remaining-message="objectNotFoundMessage"
            :disabled="!isEditable"
            :model-value="objectApiName"
            :options="objectOptions"
            @update:model-value="handleObjectChange"
          >
            <template #option="{ option }">
              <div class="product-mapping-tab__object-option">
                <span class="product-mapping-tab__object-option-label">
                  {{ option.label }}
                </span>
                <span class="product-mapping-tab__object-option-api-name">
                  {{ option.value }}
                </span>
              </div>
            </template>
          </BittsSelect>
        </template>
        <BittsDivider class="my-0" />
        <div class="product-mapping-tab__grid product-mapping-tab__headers">
          <span class="product-mapping-tab__header-label">CRM fields</span>
          <span aria-hidden="true" />
          <TextWithTooltip
            text="Crossbeam product fields"
            text-classes="text-neutral-text-weak text-sm font-bold"
            tooltip-text="These are the Crossbeam fields your CRM fields will be mapped to."
          />
        </div>
      </div>
      <BittsCallout
        v-if="fieldsStatus === 'failed'"
        size="x-small"
        type="danger"
        data-testid="product-mapping-fields-error"
        title="We couldn't load this object's fields"
        subtitle="Please try again in a few moments."
        action-text="Retry"
        class="mb-16"
        @bitts-callout-action="handleFieldsRetry"
      />
      <BittsCallout
        v-else-if="fieldsStatus === 'stale'"
        size="x-small"
        type="warning"
        data-testid="product-mapping-fields-stale"
        title="This object is no longer available"
        subtitle="It changed in Salesforce since the list loaded. Refresh the list and pick another object."
        action-text="Refresh list"
        @bitts-callout-action="handleCatalogRefresh"
      />
      <div class="product-mapping-tab__rows">
        <div
          v-for="row in PRODUCT_MAPPING_ROWS"
          :key="row.mdmProperty"
          class="product-mapping-tab__grid product-mapping-tab__row"
        >
          <BittsSelect
            :key="`${row.mdmProperty}-${selectedObjectApiName}`"
            class="product-mapping-tab__row-select"
            placeholder="Select your CRM field"
            :data-testid="row.testId"
            :loading="fieldsStatus === 'loading'"
            :disabled="areRowsDisabled"
            :model-value="rowModelValue(row.mdmProperty)"
            :options="rowOptions(row.mdmProperty)"
          />
          <FontAwesomeIcon
            :icon="['fak', 'arrow-right']"
            class="product-mapping-tab__row-arrow"
          />
          <BittsSelect
            class="product-mapping-tab__row-select"
            disabled
            :model-value="row.mdmProperty"
            :options="[{ value: row.mdmProperty, label: row.label }]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsCallout,
  BittsDivider,
  BittsLink,
  BittsSelect,
} from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { DataSourceSettingsTabType } from '@/components/data-sources/SettingsModal/constants';
import TextWithTooltip from '@/components/performance-dashboard/TextWithTooltip.vue';

import {
  CONNECTION_CHECK_ERROR_STATUS,
  SOURCE_STATUS_MAP,
} from '@/constants/data_sources';
import { MDM_PROPERTIES, MDM_TYPES, MdmPropertyValue } from '@/constants/mdm';
import { enableMocks } from '@/mocks';
import { useFeedsStore } from '@/stores';

import { useAccountProductSource } from '../composables/useAccountProductSource';
import { useObjectCatalog } from '../composables/useObjectCatalog';
import { useObjectFields } from '../composables/useObjectFields';
import { registerAccountProductMocks } from '../mockHandlers';

type Props = {
  feedId: number;
};

const props = defineProps<Props>();

if (enableMocks) registerAccountProductMocks();

const ACCOUNT_PRODUCT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.ACCOUNT_PRODUCT];

const [UNAUTHORIZED_SOURCE_STATUS] = SOURCE_STATUS_MAP.missing;

type ProductMappingRow = {
  label: string;
  mdmProperty: MdmPropertyValue<typeof MDM_TYPES.ACCOUNT_PRODUCT>;
  testId: string;
};

const PRODUCT_MAPPING_ROWS: ProductMappingRow[] = [
  {
    label: 'Account ID',
    mdmProperty: ACCOUNT_PRODUCT_PROPERTIES.COMPANY_ID,
    testId: 'product-mapping-row-company-id',
  },
  {
    label: 'Product Name',
    mdmProperty: ACCOUNT_PRODUCT_PROPERTIES.NAME,
    testId: 'product-mapping-row-name',
  },
  {
    label: 'Status',
    mdmProperty: ACCOUNT_PRODUCT_PROPERTIES.STATUS,
    testId: 'product-mapping-row-status',
  },
];

const { accountProductSource, fieldsByMdmProperty } = useAccountProductSource(
  () => props.feedId,
);

const feedsStore = useFeedsStore();
const feed = computed(() => feedsStore.getFeedById(props.feedId));

type FeedBlockReasonType = 'disconnected' | 'paused';

/* A dead token beats a pause: reconnecting is what the user has to do first. */
const feedBlockReason = computed<FeedBlockReasonType | undefined>(() => {
  if (
    feed.value?.status === CONNECTION_CHECK_ERROR_STATUS ||
    accountProductSource.value?.status === UNAUTHORIZED_SOURCE_STATUS
  ) {
    return 'disconnected';
  }
  return feed.value?.is_paused ? 'paused' : undefined;
});

const route = useRoute();
const router = useRouter();

function goToGeneralTab() {
  const { id, source_id: sourceId } = route.params;
  router.push({
    name: 'data-sources-settings-new',
    params: { id, tab: DataSourceSettingsTabType.GENERAL, source_id: sourceId },
  });
}

const isEditable = computed(() => !accountProductSource.value);

const {
  status: catalogStatus,
  objects: catalogObjects,
  load: loadCatalog,
} = useObjectCatalog(() => props.feedId);

const {
  status: fieldsStatus,
  fields: objectFields,
  load: loadFields,
  reset: resetFields,
} = useObjectFields(() => props.feedId);

type SelectOption = { value: string; label: string };

function byLabelThenApiName(a: SelectOption, b: SelectOption) {
  return a.label.localeCompare(b.label) || a.value.localeCompare(b.value);
}

const selectedObjectApiName = ref<string | undefined>(undefined);

const objectApiName = computed(() =>
  isEditable.value
    ? selectedObjectApiName.value
    : accountProductSource.value?.table,
);

const objectOptions = computed(() => {
  if (!isEditable.value) {
    const apiName = accountProductSource.value?.table;
    return apiName ? [{ value: apiName, label: apiName }] : [];
  }
  return catalogObjects.value
    .map((object) => ({ value: object.api_name, label: object.label }))
    .sort(byLabelThenApiName);
});

/* The API name is shown on every option, so it has to be searchable too. The
   default filter in BittsSelect only reads the label. */
function filterObjectOptions(search: string, option: { value: string }) {
  const term = search.trim().toLowerCase();
  const found = objectOptions.value.find(({ value }) => value === option.value);
  if (!found) return false;
  return (
    found.label.toLowerCase().includes(term) ||
    found.value.toLowerCase().includes(term)
  );
}

const fieldOptions = computed(() =>
  objectFields.value
    .map((field) => ({ value: field.api_name, label: field.label }))
    .sort(byLabelThenApiName),
);

const objectNotFoundMessage = computed(() =>
  catalogStatus.value === 'loading'
    ? 'Loading your Salesforce objects…'
    : 'No results found',
);

const areRowsDisabled = computed(() => {
  if (!isEditable.value || feedBlockReason.value) return true;
  if (!selectedObjectApiName.value) return true;
  return fieldsStatus.value !== 'loaded';
});

function savedFieldOption(mdmProperty: ProductMappingRow['mdmProperty']) {
  const field = fieldsByMdmProperty.value[mdmProperty];
  return field ? [{ value: field.column, label: field.display_name }] : [];
}

function rowOptions(mdmProperty: ProductMappingRow['mdmProperty']) {
  return isEditable.value ? fieldOptions.value : savedFieldOption(mdmProperty);
}

function rowModelValue(mdmProperty: ProductMappingRow['mdmProperty']) {
  return isEditable.value ? undefined : savedFieldOption(mdmProperty)[0]?.value;
}

/* The row selects are keyed by object, so they remount and drop the field the
   user picked for the previous one. Ant keeps that internally while the value
   we pass is undefined. */
function handleObjectChange(apiName: string) {
  selectedObjectApiName.value = apiName;
  loadFields(apiName);
}

function handleFieldsRetry() {
  if (selectedObjectApiName.value) loadFields(selectedObjectApiName.value);
}

function handleCatalogRefresh() {
  selectedObjectApiName.value = undefined;
  resetFields();
  loadCatalog();
}

watch(
  () => isEditable.value && !feedBlockReason.value,
  (canLoadCatalog) => {
    if (canLoadCatalog && catalogStatus.value === 'idle') loadCatalog();
  },
  { immediate: true },
);

// TODO: hardcoded false until the rows are interactive; should reflect real
// unsaved selections once the CRM-field selects are wired up.
const hasPendingChanges = computed(() => false);
const isSaving = ref(false);

// TODO: no-op until save is wired to POST /v0.1/feeds/{id}/sources/account-product.
function handleSaveAll() {
  return Promise.resolve();
}

defineExpose({
  hasPendingChanges,
  isSaving,
  handleSaveAll,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.product-mapping-tab {
  @apply flex flex-col gap-16;
}

.product-mapping-tab__grid {
  @apply grid grid-cols-[1fr_24px_1fr] gap-16;
}

.product-mapping-tab__headers {
  @apply mb-8;
}

.section-title {
  @apply text-neutral-text-strong text-m font-bold;
}

.section-description {
  @apply text-neutral-text;
}

.product-mapping-tab__callout-subtitle {
  @apply text-neutral-text text-sm;
}

.product-mapping-tab__header-label {
  @apply text-neutral-text-weak text-sm font-bold;
}

/* The select starts as wide as the CRM field column below it, then grows for a
   long object name rather than cutting it off. */
.product-mapping-tab__object-select {
  @apply w-fit min-w-[calc(50%-28px)] max-w-full;
}

.product-mapping-tab__object-option {
  @apply flex items-baseline justify-between gap-8 w-full min-w-0;
}

/* Salesforce object names run long, so both halves truncate rather than
   widening the select past its column. The API name gives way first. */
.product-mapping-tab__object-option-label {
  @apply truncate;
}

.product-mapping-tab__object-option-api-name {
  @apply text-neutral-text-weak text-sm truncate shrink-[2];
}

.product-mapping-tab__rows {
  @apply flex flex-col gap-8;
}

.product-mapping-tab__row {
  @apply items-center;
}

.product-mapping-tab__row-arrow {
  @apply text-neutral-accent justify-self-center;
}
</style>
